
import Vue from 'vue'
import api from '@root/src/api/api'
import Opz2Icon from './Opz2Icon.vue'

/** Код бэкенда: yClients ответил 403 на список пользователей филиала. */
const YC_USERS_NO_ACCESS = 'YCLIENTS_USERS_NO_ACCESS'

/** Тексты правил — из макета. id стабильные, их хранит бэкенд (settings_opz_kpi). */
const RULES = [
    {
        id: 'opzCreated',
        name: 'ОПЗ создана',
        desc: 'Администратор создал повторную запись в том же направлении.',
        condition: 'Любая ОПЗ',
    },
    {
        id: 'sameStaff',
        name: 'ОПЗ 2.0',
        desc: 'Тот же мастер или другой мастер того же направления.',
        condition: 'То же направление',
    },
    {
        id: 'otherStaff',
        name: 'Кросс-продажа',
        desc: 'Запись к мастеру из другой категории услуг.',
        condition: 'Другая категория',
    },
    {
        id: 'firstly',
        name: 'Впервые',
        desc: 'Новая для клиента услуга или мастер другого направления, у которого он ещё не был.',
        condition: 'Впервые = Да',
    },
    {
        id: 'sameDay',
        name: 'День в день',
        desc: 'Дата визита совпадает с датой создания записи.',
        condition: 'Визит в день записи',
    },
    {
        id: 'visitDone',
        name: 'Завершённый визит по ОПЗ',
        desc: 'Повторный визит в том же направлении состоялся.',
        condition: 'ОПЗ завершена',
    },
]

const ACCRUE_WHEN = [
    { id: 'onRecord', title: 'В момент записи' },
    { id: 'afterVisit', title: 'После визита' },
    { id: 'afterClose', title: 'После закрытия' },
]

let rowKey = 0

export default Vue.extend({
    name: 'Opz2SettingsPane',
    components: { Opz2Icon },
    props: {
        projectId: { type: String, required: true },
        kpiConfigured: { type: Boolean, default: false },
    },
    data: () => ({
        loading: true,
        error: false,
        saving: false,
        dirty: false,
        settingsError: '',
        rules: RULES,
        accrueWhen: ACCRUE_WHEN,
        actionHeads: ['Действие', 'Условие', 'Когда начислять', 'Ставка, ₽', 'Процент', 'Включено'],
        scheme: { name: '', description: '', appliesTo: '' },
        actions: {} as Record<
            string,
            { enabled: boolean; accrueWhen: string; rate: number; percent: number }
        >,
        staff: [] as Array<{ staffId: string; staffName: string; serviceType: string | null }>,
        serviceTypes: [] as string[],
        dhUsers: [] as Array<{ id: string; label: string }>,
        adminRows: [] as Array<{ key: number; dh: string; yc: string }>,
        crmUsers: [] as Array<{ id: string; label: string }>,
        crmUsersLoading: false,
        crmUsersAccessDenied: false,
    }),
    computed: {
        enabledCount(): number {
            return Object.values(this.actions).filter(a => a.enabled).length
        },
        saveStatus(): string {
            if (this.dirty) return 'Есть несохранённые изменения'
            return this.kpiConfigured ? 'Настройки сохранены' : 'Сохраните схему, чтобы запустить расчёт KPI'
        },
    },
    created() {
        this.load()
    },
    methods: {
        rulesWord(n: number) {
            const mod10 = n % 10
            const mod100 = n % 100
            if (mod10 === 1 && mod100 !== 11) return 'правило'
            if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'правила'
            return 'правил'
        },
        markDirty() {
            this.dirty = true
        },
        async load() {
            this.loading = true
            this.error = false
            try {
                const [settings, admins] = await Promise.all([
                    api.gcb2.getOpz2Settings({ projectId: this.projectId }),
                    api.gcb2.getAdminsAndDHUsers({ projectId: this.projectId }),
                ])
                if (settings.error || !settings.data) {
                    this.error = true
                    return
                }
                const scheme = settings.data.scheme ?? {}
                this.scheme = {
                    name: scheme.name ?? '',
                    description: scheme.description ?? '',
                    appliesTo: scheme.appliesTo ?? '',
                }
                this.actions = RULES.reduce((acc, rule) => {
                    const action = scheme.actions?.[rule.id] ?? {}
                    acc[rule.id] = {
                        enabled: Boolean(action.enabled),
                        accrueWhen: action.accrueWhen ?? 'onRecord',
                        rate: Number(action.rate ?? 0),
                        percent: Number(action.percent ?? 0),
                    }
                    return acc
                }, {} as Record<string, any>)
                this.$emit('scheme', this.scheme.name)
                this.serviceTypes = settings.data.serviceTypes ?? []
                this.staff = (settings.data.staff ?? []).map((row: any) => ({ ...row }))
                this.dhUsers = (admins.data?.dhUsers ?? []).map((user: any) => ({
                    id: String(user.id),
                    label: user.name || user.email || String(user.id),
                }))
                this.adminRows = (admins.data?.currentSettings ?? [])
                    .filter((item: any) => item.crmUserId)
                    .map((item: any) => ({ key: ++rowKey, dh: String(item.id), yc: String(item.crmUserId) }))
                this.dirty = false
                this.loadCrmUsers()
            } catch (e) {
                this.error = true
            } finally {
                this.loading = false
            }
        },
        async loadCrmUsers() {
            this.crmUsersLoading = true
            try {
                const res = await api.gcb2.getOpz2YcUsers({ projectId: this.projectId })
                this.crmUsersAccessDenied = res.error?.message === YC_USERS_NO_ACCESS
                this.crmUsers = (res.data?.users ?? []).map((user: any) => ({
                    id: String(user.id),
                    label: user.email ? `${user.name} (${user.email})` : user.name,
                }))
            } catch (e) {
                this.crmUsersAccessDenied = false
                this.crmUsers = []
            } finally {
                this.crmUsersLoading = false
            }
        },
        // связь 1:1 — уже выбранные в других строках варианты скрываем
        dhOptions(row: { dh: string }) {
            return this.dhUsers.filter(
                user => user.id === row.dh || !this.adminRows.some(r => r.dh === user.id)
            )
        },
        ycOptions(row: { yc: string }) {
            const options = this.crmUsers.filter(
                user => user.id === row.yc || !this.adminRows.some(r => r.yc === user.id)
            )
            // сохранённый сотрудник мог не прийти из YCLIENTS — всё равно показываем его id
            if (row.yc && !options.some(user => user.id === row.yc))
                options.unshift({ id: row.yc, label: row.yc })
            return options
        },
        addRow() {
            this.adminRows.push({ key: ++rowKey, dh: '', yc: '' })
            this.markDirty()
        },
        removeRow(index: number) {
            this.adminRows.splice(index, 1)
            this.markDirty()
        },
        validate(): string {
            if (!this.scheme.name.trim()) return 'Укажите название схемы.'
            const invalid = Object.values(this.actions).some(
                a =>
                    a.enabled &&
                    (!Number.isFinite(a.rate) ||
                        a.rate < 0 ||
                        a.rate > 100000 ||
                        !Number.isFinite(a.percent) ||
                        a.percent < 0 ||
                        a.percent > 100)
            )
            if (invalid) return 'Проверьте ставки: сумма — от 0 до 100 000 ₽, процент — от 0 до 100%.'
            const incomplete = this.adminRows.some(r => !r.dh || !r.yc)
            const duplicate =
                new Set(this.adminRows.map(r => r.dh)).size !== this.adminRows.length ||
                new Set(this.adminRows.map(r => r.yc)).size !== this.adminRows.length
            if (incomplete || duplicate) return 'Заполните сопоставления администраторов без повторений.'
            if (this.staff.some(row => !row.serviceType)) return 'Выберите направление для каждого мастера.'
            return ''
        },
        async save() {
            this.settingsError = this.validate()
            if (this.settingsError) return
            this.saving = true
            const firstSetup = !this.kpiConfigured
            try {
                const mapped = new Map(this.adminRows.map(r => [r.dh, r.yc]))
                const [settingsRes, mappingRes] = await Promise.all([
                    api.gcb2.saveOpz2Settings({
                        projectId: this.projectId,
                        settings: {
                            name: this.scheme.name,
                            appliesTo: this.scheme.appliesTo,
                            description: this.scheme.description,
                            actions: this.actions,
                            staffServiceTypes: this.staff.map(row => ({
                                staffId: row.staffId,
                                serviceType: row.serviceType ?? null,
                            })),
                        },
                    }),
                    // строки, которые удалили, сохраняем как «без сопоставления»
                    this.crmUsersAccessDenied
                        ? Promise.resolve({ error: null })
                        : api.gcb2.saveAdminIdToUser({
                              settings: this.dhUsers.map(user => ({
                                  userId: user.id,
                                  crmId: mapped.get(user.id) ?? null,
                              })),
                          }),
                ])
                if (settingsRes.error || (mappingRes as any).error) {
                    this.settingsError = 'Не удалось сохранить настройки. Попробуйте ещё раз.'
                    return
                }
                this.dirty = false
                this.$emit('scheme', this.scheme.name)
                this.$emit('saved', { firstSetup })
            } catch (e) {
                this.settingsError = 'Не удалось сохранить настройки. Попробуйте ещё раз.'
            } finally {
                this.saving = false
            }
        },
    },
})
